import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t, type IntlService } from 'ember-intl';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { PhPlus, PhTrash, PhPencil, PhGraduationCap } from 'ember-phosphor-icons';
import AlertBanner from 'tio-ui/components/alert-banner';
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord, deleteExistingRecord } from 'tio-data/requests/save-record';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  type FieldName,
} from 'tio-common/utils/tuition-assistance/fields';

type FieldConfig = {
  visible: boolean;
  required: boolean;
  label: string;
  type: 'string' | 'number' | 'currency';
  name: string;
};

interface CourseEditorSignature {
  Args: {
    /** The TAS application to add/edit courses on */
    application: TasApplicationModel;
    /** The program template controlling field visibility/requirements */
    programTemplate: TasProgramTemplateModel;
  };
  Element: HTMLDivElement;
}

const COURSE_FIELD_DEFS: Array<{
  name: string;
  type: 'string' | 'number' | 'currency';
}> = [
  { name: 'COURSE_NAME', type: 'string' },
  { name: 'COURSE_NUMBER', type: 'string' },
  { name: 'COURSE_DESCRIPTION', type: 'string' },
  { name: 'COURSE_CREDIT', type: 'number' },
  { name: 'COURSE_TUITION', type: 'currency' },
  { name: 'COURSE_BOOKS', type: 'currency' },
  { name: 'COURSE_LABS', type: 'currency' },
  { name: 'COURSE_FEES', type: 'currency' },
  { name: 'COURSE_SOFTWARE', type: 'currency' },
];

/**
 * CourseEditor - Reimagined inline course management
 *
 * Provides card-based course listing with inline add/edit forms.
 * Reads field configuration from the program template to show only
 * relevant fields.
 *
 * @example
 * ```gts
 * <CourseEditor
 *   @application={{this.application}}
 *   @programTemplate={{this.programTemplate}}
 * />
 * ```
 */
export default class CourseEditor extends Component<CourseEditorSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked editingCourse: TasCourseModel | null = null;
  @tracked isAddingNew = false;
  @tracked fieldErrors: Record<string, string> = {};

  get courses(): TasCourseModel[] {
    return this.args.application.activeCourses?.slice() ?? [];
  }

  get visibleFields(): FieldConfig[] {
    const templateFields = this.args.programTemplate.fields as Record<
      string,
      { visible?: boolean; required?: boolean; label?: string }
    >;
    return COURSE_FIELD_DEFS.filter((def) => {
      const config = templateFields?.[def.name];
      return config?.visible;
    }).map((def) => {
      const config = templateFields[def.name]!;
      return {
        name: def.name,
        type: def.type,
        visible: true,
        required: !!config.required,
        label: config.label || def.name,
      };
    });
  }

  get hasCourses() {
    return this.courses.length > 0;
  }

  getFieldValue = (course: TasCourseModel, fieldName: string) => {
    return getSingleValueForTasField(fieldName as FieldName, course.fields) as
      | string
      | number
      | undefined;
  };

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });

  addCourse = () => {
    this.editingCourse = this.store.createRecord('tas-course', {
      tasApplication: this.args.application,
      fields: this.args.programTemplate.courseFieldsHash,
      customFields: this.args.programTemplate.courseCustomFields,
    }) as TasCourseModel;
    this.isAddingNew = true;
    this.fieldErrors = {};
  };

  editCourse = (course: TasCourseModel) => {
    this.editingCourse = course;
    this.isAddingNew = false;
    this.fieldErrors = {};
  };

  setFieldValue = (fieldName: string, event: Event) => {
    if (!this.editingCourse) return;
    const target = event.target as HTMLInputElement;
    const value = target.value;

    const fieldConfig = this.visibleFields.find((field) => field.name === fieldName);
    let storedValue: string | number = value;

    if (fieldConfig?.type === 'currency') {
      const cleaned = value.replace(/[^0-9.-]/g, '');
      const numeric = Number(cleaned);
      if (!Number.isNaN(numeric)) {
        storedValue = Math.round(numeric * 100);
      }
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      storedValue,
      this.editingCourse.fields
    );

    // Clear error on edit
    const errors = { ...this.fieldErrors };
    delete errors[fieldName];
    this.fieldErrors = errors;
  };

  validateAndSave = async () => {
    if (!this.editingCourse) return;
    const errors: Record<string, string> = {};

    for (const field of this.visibleFields) {
      if (field.required) {
        const value = this.getFieldValue(this.editingCourse, field.name);
        if (value === undefined || value === '' || value === null) {
          errors[field.name] = this.intl.t(
            'tuition_assistance.reimagined.wizard.course.field_required'
          );
        }
      }
      if (field.type === 'number' || field.type === 'currency') {
        const value = this.getFieldValue(this.editingCourse, field.name);
        if (value !== undefined && value !== '' && isNaN(Number(value))) {
          errors[field.name] = this.intl.t(
            'tuition_assistance.reimagined.wizard.course.field_number'
          );
        }
      }
    }

    this.fieldErrors = errors;
    if (Object.keys(errors).length > 0) return;

    await saveRecord(this.store, this.editingCourse);
    this.editingCourse = null;
    this.isAddingNew = false;
  };

  cancelEdit = () => {
    if (this.isAddingNew && this.editingCourse) {
      this.editingCourse.unloadRecord();
    }
    this.editingCourse = null;
    this.isAddingNew = false;
    this.fieldErrors = {};
  };

  removeCourse = async (course: TasCourseModel) => {
    await deleteExistingRecord(this.store, course);
  };

  <template>
    <div class="space-y-4" ...attributes>
      <div class="tio-row-between">
        <div class="tio-row-tight">
          <PhGraduationCap @size={{20}} class="tio-fg-info" />
          <Heading @level={{3}}>
            {{t "tuition_assistance.reimagined.wizard.course.title"}}
          </Heading>
        </div>
        {{#unless this.editingCourse}}
          <Button @intent="primary" @appearance="outlined" @size="sm" {{on "click" this.addCourse}}>
            <PhPlus @size={{16}} class="mr-1" />
            {{t "tuition_assistance.reimagined.wizard.course.add_button"}}
          </Button>
        {{/unless}}
      </div>

      {{! Existing courses list }}
      {{#each this.courses as |course|}}
        {{#if (this.isEditingThis course)}}
          {{! Inline edit form - rendered below }}
        {{else}}
          <div
            class="rounded-xl border tio-border-default tio-bg-surface p-4 tio-row-start-between gap-4"
          >
            <div class="tio-fill-min">
              <Text @weight="semibold">
                {{this.getCourseName course}}
              </Text>
              {{#if (this.getFieldValue course "COURSE_TUITION")}}
                <Text @variant="muted" @size="sm">
                  {{this.formatCurrency (this.getNumericValue course "COURSE_TUITION")}}
                </Text>
              {{/if}}
              {{#if (this.getFieldValue course "COURSE_CREDIT")}}
                <Text @variant="muted" @size="sm">
                  {{this.getFieldValue course "COURSE_CREDIT"}}
                  {{t "tuition_assistance.reimagined.wizard.course.credits"}}
                </Text>
              {{/if}}
            </div>
            <div class="tio-flex-tight shrink-0">
              <button
                type="button"
                class="p-1.5 rounded-lg tio-fg-disabled hover:tio-fg-info-body hover:tio-bg-info-soft-hover"
                {{on "click" (fn this.editCourse course)}}
              >
                <PhPencil @size={{16}} />
              </button>
              <button
                type="button"
                class="p-1.5 rounded-lg tio-fg-disabled hover:tio-fg-danger-body hover:tio-bg-danger-soft-hover"
                {{on "click" (fn this.removeCourse course)}}
              >
                <PhTrash @size={{16}} />
              </button>
            </div>
          </div>
        {{/if}}
      {{/each}}

      {{! Empty state }}
      {{#unless this.hasCourses}}
        {{#unless this.editingCourse}}
          <div
            class="rounded-xl border-2 border-dashed tio-border-default tio-bg-sunken p-8 text-center"
          >
            <PhGraduationCap @size={{32}} class="mx-auto tio-fg-disabled mb-2" />
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.course.empty_message"}}
            </Text>
          </div>
        {{/unless}}
      {{/unless}}

      {{! Inline edit/add form }}
      {{#if this.editingCourse}}
        <div class="rounded-xl border-2 tio-border-info bg-indigo-50/30 p-5 space-y-4">
          <Heading @level={{4}}>
            {{#if this.isAddingNew}}
              {{t "tuition_assistance.reimagined.wizard.course.add_heading"}}
            {{else}}
              {{t "tuition_assistance.reimagined.wizard.course.edit_heading"}}
            {{/if}}
          </Heading>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            {{#each this.visibleFields as |field|}}
              <div class={{if (this.isFullWidth field.name) "md:col-span-2" ""}}>
                <label class="block tio-text-sm-medium tio-fg-secondary mb-1">
                  {{field.label}}
                  {{#if field.required}}
                    <span class="tio-fg-danger">*</span>
                  {{/if}}
                </label>
                {{#if (this.isTextarea field.name)}}
                  <textarea
                    class="w-full rounded-lg border tio-border-default px-3 py-2 text-sm focus:tio-border-brand focus:ring-1 focus:tio-ring-brand
                      {{if (this.hasError field.name) 'tio-border-danger'}}"
                    value={{this.getFieldValue this.editingCourse field.name}}
                    rows="2"
                    {{on "input" (fn this.setFieldValue field.name)}}
                  />
                {{else}}
                  <input
                    type={{if (this.isNumericField field.type) "text" "text"}}
                    inputmode={{if (this.isNumericField field.type) "decimal" "text"}}
                    class="w-full rounded-lg border tio-border-default px-3 py-2 text-sm focus:tio-border-brand focus:ring-1 focus:tio-ring-brand
                      {{if (this.hasError field.name) 'tio-border-danger'}}"
                    value={{this.getFieldValue this.editingCourse field.name}}
                    {{on "input" (fn this.setFieldValue field.name)}}
                  />
                {{/if}}
                {{#if (this.hasError field.name)}}
                  <Text @size="xs" class="tio-fg-danger mt-1">
                    {{this.getError field.name}}
                  </Text>
                {{/if}}
              </div>
            {{/each}}
          </div>

          <div class="tio-flex-end gap-3 pt-2">
            <Button @appearance="outlined" {{on "click" this.cancelEdit}}>
              {{t "common.cancel"}}
            </Button>
            <Button @intent="primary" {{on "click" this.validateAndSave}}>
              {{#if this.isAddingNew}}
                {{t "tuition_assistance.reimagined.wizard.course.save_button"}}
              {{else}}
                {{t "tuition_assistance.reimagined.wizard.course.update_button"}}
              {{/if}}
            </Button>
          </div>
        </div>
      {{/if}}

      {{#if this.hasCourses}}
        <AlertBanner @intent="success" @size="sm">
          {{t
            "tuition_assistance.reimagined.wizard.course.count_message"
            count=this.courses.length
          }}
        </AlertBanner>
      {{/if}}
    </div>
  </template>

  isEditingThis = (course: TasCourseModel) => {
    return this.editingCourse === course && !this.isAddingNew;
  };

  getCourseName = (course: TasCourseModel) => {
    const name = this.getFieldValue(course, 'COURSE_NAME') as string;
    const number = this.getFieldValue(course, 'COURSE_NUMBER') as string;
    if (name && number) return `${number} - ${name}`;
    return name || number || this.intl.t('tuition_assistance.reimagined.wizard.course.unnamed');
  };

  getNumericValue = (course: TasCourseModel, fieldName: string) => {
    const val = this.getFieldValue(course, fieldName);
    return Number(val) || 0;
  };

  isFullWidth = (fieldName: string) => {
    return fieldName === 'COURSE_DESCRIPTION';
  };

  isTextarea = (fieldName: string) => {
    return fieldName === 'COURSE_DESCRIPTION';
  };

  isNumericField = (type: string) => {
    return type === 'number' || type === 'currency';
  };

  hasError = (fieldName: string) => {
    return !!this.fieldErrors[fieldName];
  };

  getError = (fieldName: string) => {
    return this.fieldErrors[fieldName] ?? '';
  };
}
